import type { VendorWasteLineAlignmentCandidatesQuery } from "generated-graphql/graphql";

import { safeParseInt } from "encamp-shared/src/utils/safeParseNumber";
import { formatWasteQuantity } from "encamp-shared/src/waste/eManifestWasteUnit";

export type AlignmentState =
  VendorWasteLineAlignmentCandidatesQuery["vendorWasteLineAlignmentCandidates"];
export type AlignmentLine = AlignmentState["eManifestLines"][number];

/** The value a select carries for "this line has no counterpart". */
export const NO_VENDOR_LINE = "__none__";

/** e-Manifest line number -> the vendor line number chosen for it, or NO_VENDOR_LINE. */
export type PairingChoices = Record<string, string>;

/**
 * Where the reviewer starts: the pairing already recorded, or pairing by line
 * number where none is. The same starting point the pipeline uses, so the
 * reviewer edits what the findings were computed from.
 */
export function initialChoices(state: AlignmentState): PairingChoices {
  const vendorNumbers = new Set(state.vendorLines.map((v) => v.wasteLineNumber));
  const choices: PairingChoices = {};
  for (const e of state.eManifestLines) {
    choices[e.wasteLineNumber] = vendorNumbers.has(e.wasteLineNumber)
      ? e.wasteLineNumber
      : NO_VENDOR_LINE;
  }
  // Only for lines the manifest still has, on both sides, which is the same
  // rule applyPairs follows for a proposed pairing.
  //
  // A pair naming an e-Manifest line that has since been removed would leave a
  // choice nobody can see, which still counts as claiming its vendor line, so
  // pairsToSave would omit that vendor line and the mutation would refuse the
  // answer as incomplete.
  //
  // A pair naming a vendor line that has since been removed is worse: the
  // reviewer sees a slot filled with a line that is not on the manifest, and
  // Save sends that number back. A stored pairing that has stopped describing
  // the manifest still arrives here, because only confirmedAt is withheld when
  // it stops applying, so this is where it has to be caught.
  for (const pair of state.confirmedPairs ?? []) {
    const eLine = pair.eManifestLine;
    if (eLine == null || choices[eLine] === undefined) continue;
    const vendorLine = pair.vendorLine;
    choices[eLine] =
      vendorLine != null && vendorNumbers.has(vendorLine) ? vendorLine : NO_VENDOR_LINE;
  }
  return choices;
}

/** Vendor lines the reviewer has pointed more than one e-Manifest line at. */
export function duplicateVendorLines(choices: PairingChoices): Set<string> {
  const counts = new Map<string, number>();
  for (const value of Object.values(choices)) {
    if (value !== NO_VENDOR_LINE) counts.set(value, (counts.get(value) ?? 0) + 1);
  }
  return new Set([...counts.entries()].filter(([, n]) => n > 1).map(([value]) => value));
}

/** Vendor lines no e-Manifest line is pointing at. */
export function unclaimedVendorLines(
  state: AlignmentState,
  choices: PairingChoices
): AlignmentLine[] {
  const claimed = new Set(Object.values(choices).filter((value) => value !== NO_VENDOR_LINE));
  return state.vendorLines.filter((v) => !claimed.has(v.wasteLineNumber));
}

/**
 * The complete decision to store: one pair per e-Manifest line, plus one for
 * each vendor line nobody claimed. The mutation requires every line on both
 * sides to appear exactly once, and deriving the leftovers here means the
 * reviewer does not have to say the same thing twice.
 */
export function pairsToSave(
  state: AlignmentState,
  choices: PairingChoices
): { eManifestLine: string | null; vendorLine: string | null }[] {
  return [
    ...state.eManifestLines.map((e) => {
      const chosen = choices[e.wasteLineNumber];
      return {
        eManifestLine: e.wasteLineNumber,
        vendorLine: chosen == null || chosen === NO_VENDOR_LINE ? null : chosen,
      };
    }),
    ...unclaimedVendorLines(state, choices).map((v) => ({
      eManifestLine: null,
      vendorLine: v.wasteLineNumber,
    })),
  ];
}

/** One line's comparable values, on one line of text. */
export function lineSummary(line: AlignmentLine | undefined): string {
  if (!line) return "—";
  const containers =
    line.containerCount != null && line.containerTypeCode
      ? `${line.containerCount} × ${line.containerTypeCode}`
      : line.containerTypeCode;
  const parts = [
    line.quantity != null
      ? formatWasteQuantity(line.quantity, line.quantityUnitOfMeasureCode)
      : null,
    containers,
    line.managementMethodCode,
    line.federalWasteCodes.length > 0 ? line.federalWasteCodes.join(" ") : null,
  ].filter((part): part is string => part != null && part !== "");
  return parts.length > 0 ? parts.join(" · ") : "no values recorded";
}

/**
 * Which of a pair's values agree, as evidence beside a proposed pairing.
 *
 * Deliberately not a score. On the live manifests carrying this problem,
 * scoring the candidate pairings leaves 17% tied outright, and of the rest
 * only a third produce a winner with no remaining disagreement — a ranked
 * suggestion would be wrong more often than right, and being wrong is what a
 * reviewer would press. Quantity uses the comparator's own 10% tolerance.
 */
export function agreementNote(e: AlignmentLine, v: AlignmentLine | undefined): string | null {
  if (!v) return null;
  const agrees: string[] = [];
  const differs: string[] = [];

  const sameUnit = e.quantityUnitOfMeasureCode === v.quantityUnitOfMeasureCode;
  if (e.quantity != null && v.quantity != null && sameUnit) {
    const hi = Math.max(Math.abs(e.quantity), Math.abs(v.quantity));
    const within = hi === 0 || Math.abs(e.quantity - v.quantity) / hi <= 0.1;
    (within ? agrees : differs).push("quantity");
  }
  if (e.containerTypeCode && v.containerTypeCode) {
    (e.containerTypeCode === v.containerTypeCode ? agrees : differs).push("container");
  }
  if (e.managementMethodCode && v.managementMethodCode) {
    (e.managementMethodCode === v.managementMethodCode ? agrees : differs).push("method");
  }

  if (agrees.length === 0 && differs.length === 0) return "nothing comparable";
  return [
    agrees.length > 0 ? `${agrees.join(", ")} agree` : null,
    differs.length > 0 ? `${differs.join(", ")} differ` : null,
  ]
    .filter((part): part is string => part != null)
    .join("; ");
}

/**
 * Whether Save should be offered.
 *
 * The starting point the ledger shows is pairing by number, which is already
 * the right answer whenever one source dropped a line without renumbering, or
 * one side has an extra line on the end. Requiring an edit first would leave
 * those manifests with no way to record a pairing at all, so their findings
 * would stay marked unmatchable forever. Once a pairing is stored, Save is for
 * changing it.
 */
export function canRecordPairing(dirty: boolean, confirmedAt: string | null | undefined): boolean {
  return dirty || confirmedAt == null;
}

/**
 * The comparable fields, in the order the matching screen shows them.
 *
 * Every one carries its label. A row of bare values reads as a code nobody can
 * place: `1 × DF` and `H141` are the EPA codes off the form, which is the right
 * thing to show a person who works with the form, but only once something says
 * which box they came from.
 */
export const PAIR_FIELDS = [
  {
    key: "quantity",
    label: "Quantity",
    value: (l: AlignmentLine) => formatWasteQuantity(l.quantity, l.quantityUnitOfMeasureCode),
  },
  {
    key: "containers",
    label: "Containers",
    value: (l: AlignmentLine) => {
      if (!l.containerTypeCode) return "—";
      return l.containerCount != null
        ? `${l.containerCount} × ${l.containerTypeCode}`
        : l.containerTypeCode;
    },
  },
  {
    key: "method",
    label: "Method",
    value: (l: AlignmentLine) => l.managementMethodCode ?? "—",
  },
  {
    key: "codes",
    label: "Waste codes",
    value: (l: AlignmentLine) =>
      l.federalWasteCodes.length > 0 ? l.federalWasteCodes.join(" ") : "—",
  },
] as const;

export type PairFieldKey = (typeof PAIR_FIELDS)[number]["key"];

/**
 * Which of a pair's fields disagree, so the table can mark the value rather
 * than make the reader compare two strings.
 *
 * Compared as displayed. A quantity recorded in different units reads as a
 * difference, which is what it is: these are two sources describing one waste,
 * and the unit is part of the description.
 */
export function differingFields(e: AlignmentLine, v: AlignmentLine | undefined): Set<PairFieldKey> {
  const out = new Set<PairFieldKey>();
  if (!v) return out;
  for (const field of PAIR_FIELDS) {
    if (field.value(e) !== field.value(v)) out.add(field.key);
  }
  return out;
}

/** One line of the matching screen: a pair, or a vendor line nothing claimed. */
export type PairingRow =
  | { kind: "pair"; eLine: AlignmentLine; vLine: AlignmentLine | undefined }
  | { kind: "vendorOnly"; vLine: AlignmentLine };

/**
 * The rows in reading order.
 *
 * e-Manifest lines keep their own order, and a vendor line nobody claimed is
 * placed just before the first matched vendor line that outranks it, so it
 * sits where the vendor's own numbering puts it rather than in a block at the
 * end. That position is what makes a dropped line legible: the gap appears
 * next to the lines it is between.
 */
export function pairingRows(state: AlignmentState, choices: PairingChoices): PairingRow[] {
  const vendorByNumber = new Map(state.vendorLines.map((v) => [v.wasteLineNumber, v]));
  const unplaced = unclaimedVendorLines(state, choices).sort(compareLineNumbers);
  const rows: PairingRow[] = [];

  const placeBefore = (limit: string | null) => {
    while (unplaced.length > 0) {
      const next = unplaced[0];
      if (next == null) break;
      if (limit != null && compareLineNumbers(next, { wasteLineNumber: limit }) >= 0) break;
      unplaced.shift();
      rows.push({ kind: "vendorOnly", vLine: next });
    }
  };

  for (const eLine of state.eManifestLines) {
    const chosen = choices[eLine.wasteLineNumber];
    if (chosen != null && chosen !== NO_VENDOR_LINE) placeBefore(chosen);
    rows.push({
      kind: "pair",
      eLine,
      vLine: chosen != null ? vendorByNumber.get(chosen) : undefined,
    });
  }
  placeBefore(null);

  return rows;
}

/** Numeric where both are numbers, so "10" sorts after "9". */
function compareLineNumbers(
  a: { wasteLineNumber: string },
  b: { wasteLineNumber: string }
): number {
  const na = safeParseInt(a.wasteLineNumber);
  const nb = safeParseInt(b.wasteLineNumber);
  if (na != null && nb != null && na !== nb) return na - nb;
  return a.wasteLineNumber.localeCompare(b.wasteLineNumber);
}

/**
 * Every field of a waste line, for reading one source on its own.
 *
 * Longer than PAIR_FIELDS, which is trimmed to what a matching decision turns
 * on. This is the whole line as its source recorded it, including the state
 * waste codes and the full shipping description, for the reader who wants to
 * see what they are matching rather than the four values that distinguish it.
 */
export const LINE_DETAIL_FIELDS = [
  ...PAIR_FIELDS,
  {
    key: "stateCodes",
    label: "State codes",
    value: (l: AlignmentLine) => (l.stateWasteCodes.length > 0 ? l.stateWasteCodes.join(" ") : "—"),
  },
  {
    key: "description",
    label: "Shipping description",
    value: (l: AlignmentLine) => l.description ?? "—",
  },
] as const;

/**
 * A stored list of pairs read back as the reviewer's choices.
 *
 * The entry point an automatic first pass would write through: it proposes
 * pairs, this turns them into the same state a person edits by hand, and the
 * reviewer keeps every control they had. Every e-Manifest line starts paired
 * with nothing, so a list that names only some of them still produces a
 * complete answer. A pair naming a line number neither side has is dropped,
 * because a choice nobody can see still counts as claiming a vendor line and
 * would leave pairsToSave omitting it.
 */
export function applyPairs(
  state: AlignmentState,
  pairs: readonly { eManifestLine?: string | null; vendorLine?: string | null }[]
): PairingChoices {
  const vendorNumbers = new Set(state.vendorLines.map((v) => v.wasteLineNumber));
  const choices: PairingChoices = {};
  for (const e of state.eManifestLines) choices[e.wasteLineNumber] = NO_VENDOR_LINE;
  for (const pair of pairs) {
    const eLine = pair.eManifestLine;
    if (eLine == null || choices[eLine] === undefined) continue;
    const vendorLine = pair.vendorLine;
    choices[eLine] =
      vendorLine != null && vendorNumbers.has(vendorLine) ? vendorLine : NO_VENDOR_LINE;
  }
  return choices;
}

/** The e-Manifest line currently holding a vendor line, or undefined when nothing does. */
function holderOf(choices: PairingChoices, vendorLine: string): string | undefined {
  return Object.keys(choices).find((eLine) => choices[eLine] === vendorLine);
}

/**
 * Put a vendor line on an e-Manifest line, and give whatever was there back.
 *
 * A slot holds one vendor line, so dropping onto an occupied one has to say
 * where the card that was in it went. It goes to the slot the dragged card
 * came from, which is a swap; when the card came from no slot at all, the
 * displaced one simply stops being claimed and reappears in its own row. Never
 * a silent discard, and never a second claim on one vendor line, so a drag can
 * not produce the duplicate that blocks Save.
 */
export function placeVendorLine(
  choices: PairingChoices,
  vendorLine: string,
  targetELine: string
): PairingChoices {
  const displaced = choices[targetELine];
  // A line the manifest does not have, or the card already sitting there.
  if (displaced === undefined || displaced === vendorLine) return choices;

  const next: PairingChoices = { ...choices, [targetELine]: vendorLine };
  const source = holderOf(choices, vendorLine);
  if (source != null) next[source] = displaced;
  return next;
}

/**
 * The select's answer, routed through the transition a drop makes. Choosing a
 * line another row already holds swaps the two rather than claiming it twice,
 * so a reviewer working from the keyboard reaches the same states as one
 * working from the pointer.
 */
export function chooseVendorLine(
  choices: PairingChoices,
  targetELine: string,
  value: string
): PairingChoices {
  if (choices[targetELine] === undefined) return choices;
  if (value === NO_VENDOR_LINE) return { ...choices, [targetELine]: NO_VENDOR_LINE };
  return placeVendorLine(choices, value, targetELine);
}

/** Take a vendor line off whichever e-Manifest line holds it. */
export function unpairVendorLine(choices: PairingChoices, vendorLine: string): PairingChoices {
  const holder = holderOf(choices, vendorLine);
  if (holder == null) return choices;
  return { ...choices, [holder]: NO_VENDOR_LINE };
}
